import axios from 'axios';

import { isLocal, isProduction } from '@/consts/env';

import type { CmsContentResponse } from './types';

const getCmsBaseUrl = () => {
  if (isProduction) return 'https://zep.us/cms';
  // LOC 에서 상대 경로를 쓰는 건 **브라우저 한정**이다 — Next rewrites 로 프록시해
  // CORS 를 피한다. 서버에는 origin 도 rewrites 도 없어 axios 가 ERR_INVALID_URL 로
  // 즉시 던지고, 호출부의 catch 가 그걸 404 로 덮어 원인이 안 보인다.
  if (isLocal)
    return typeof window === 'undefined' ? 'https://dev-3.zep.us/cms' : '/cms';
  return 'https://dev-3.zep.us/cms';
};

const CMS_BASE_URL = getCmsBaseUrl();

/**
 * zep-cms S3 기반 CMS 클라이언트
 * CDN URL: PRD → https://zep.us/cms, LOC → /cms (프록시), 그 외 → https://dev-3.zep.us/cms
 */
const CMS_INSTANCE = axios.create({
  baseURL: CMS_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // 비필수 콘텐츠 전용. zep.us/cms S3 가 간헐적으로 길게 hang 하는데(WEB-APP-1QJ) 15s 는 너무 길어
  // hang 한 유저가 오래 헛대기했다. 8s 로 끊고 재시도로 회수한다(공유 CMS 인스턴스와 동일 기준).
  timeout: 8000,
});

/**
 * Response interceptor — isProduction 에 따라 json 또는 jsonDev 를 고른다.
 *
 * 예전엔 여기서 weegloo 절대 URL 을 우리 주소로 치환했다(`replaceWeeglooCdnUrl`).
 * 그 치환은 **파일이 우리 S3 에 있는지 보지 않아서**, 콘텐츠에 새 weegloo URL 이
 * 들어오는 순간 그 이미지가 프로덕션에서 깨졌다(403). 지금은 CMS 콘텐츠의 URL 자체를
 * 새 주소로 재작성해 두었으므로 치환이 필요 없고, 모르는 URL 은 원래 출처로 가서 뜬다.
 */
CMS_INSTANCE.interceptors.response.use(
  response => {
    const data = response.data as CmsContentResponse;

    // isProduction에 따라 json/jsonDev 선택
    const jsonString = isProduction ? data.json : data.jsonDev;

    // json이 없으면 jsonDev로 fallback
    const finalJsonString = jsonString ?? data.jsonDev ?? data.json;

    try {
      response.data = JSON.parse(finalJsonString);
    } catch {
      console.error('[CMS] JSON 파싱 실패');
      throw new Error('CMS 콘텐츠 파싱 실패');
    }

    return response;
  },
  error => {
    console.error('[CMS] API 에러:', error);
    return Promise.reject(error);
  },
);

/**
 * CMS 콘텐츠 조회 fetcher
 * @param params.contentId - 콘텐츠 ID
 * @returns 파싱된 콘텐츠 데이터
 */
export const getCmsContent = async <T = CmsContentResponse>({
  contentId,
}: {
  contentId: string;
}): Promise<T> => {
  const response = await CMS_INSTANCE.get<T>(`/${contentId}`);
  return response.data;
};

export { CMS_INSTANCE };
